import { useCallback, useContext, useMemo } from 'react';

import AnalyticsContext from 'contexts/AnalyticsContext';
import BaseViewsContext from 'contexts/BaseViewsContext';
import FeatureGatesContext from 'contexts/FeatureGatesContext';
import MetricOptionsContext from 'contexts/MetricOptionsContext';
import { InsightsChatContext } from 'contextProviders/InsightsChatProvider';
import InsightsViewScopeContext from 'contextProviders/InsightsContentProvider/InsightsViewScopeContext';
import { InsightsViewDrillTarget } from 'contextProviders/InsightsContentProvider/viewTypes';
import useInsightsChatBudget from 'hooks/useInsightsChatBudget';

import buildExplainValueQuestion from './buildExplainValueQuestion';
import buildInsightsFocus from './buildInsightsFocus';

export type ExplainValueSource = 'menu' | 'picker';

export interface ExplainValueArgs {
  // The filter the click would have applied (see buildInsightsFocus).
  drillDown?: FixedFilter;
  term?: string;
  metricIds: string[];
  chartDef?: VisualisationDefinition;
  cardId: string;
  gadgetTitle: string;
  drillTarget?: InsightsViewDrillTarget;
  source: ExplainValueSource;
}

// The one place a clicked chart point / matrix cell turns into an Insights
// question about that value. Shared by the dashboard click menu's "Explain this
// value" row and the report-side picker, so both open the chat with the same
// focus and the same seeded question.
//
// Available only where the chat itself is (account gate + per-user budget) AND
// under a live Insights view scope: entityId is null in popup reports,
// wallboards, the Explore modal, gadget previews and Storybook, none of which
// have a view for the focus to attach to.
const useExplainValue = (): {
  isAvailable: boolean;
  explain: (args: ExplainValueArgs) => void;
} => {
  const { isAiInsightsChatEnabled } = useContext(FeatureGatesContext);
  const { isEligible } = useInsightsChatBudget();
  const { entityId } = useContext(InsightsViewScopeContext);
  const { open, explainPicker } = useContext(InsightsChatContext);
  const { trackEvent } = useContext(AnalyticsContext);
  const { baseViews } = useContext(BaseViewsContext);
  const { metricOptionsLookup } = useContext(MetricOptionsContext);

  const isAvailable = !!isAiInsightsChatEnabled && isEligible && !!entityId;

  const disarm = explainPicker.disarm;
  const explain = useCallback(
    ({
      drillDown,
      term,
      metricIds,
      chartDef,
      cardId,
      gadgetTitle,
      drillTarget,
      source,
    }: ExplainValueArgs) => {
      if (!entityId) {
        return;
      }
      const metrics = [...new Set(metricIds)].map((id) => {
        const name = metricOptionsLookup[id]?.name;
        return { id, ...(name ? { name } : {}) };
      });
      const focus = buildInsightsFocus({
        drillDown,
        term,
        metrics,
        chartDef,
        cardId,
        entityId,
        gadgetTitle,
        baseViews,
        drillTarget,
      });
      const question = buildExplainValueQuestion({
        metricNames: metrics
          .map((m) => m.name)
          .filter((name): name is string => !!name),
        valueLabel: focus.value?.label,
        gadgetTitle,
      });
      trackEvent('Insights - Explain Value', { source });
      // One-shot: a picked value consumes the armed picker.
      disarm();
      open({ focus, question });
    },
    [entityId, metricOptionsLookup, baseViews, trackEvent, disarm, open],
  );

  return useMemo(() => ({ isAvailable, explain }), [isAvailable, explain]);
};

export default useExplainValue;
